/**
 * Kontakt-Detail-Route (W6, 2026-07-06).
 *
 * Route:  /admin/adressbuch/contacts/:id
 * Analog zu AdressbuchPersonAkte (Mitarbeiter). Bringt den Kunden-Kontext
 * auf eine eigene Route mit Sticky-Header-Bearbeiten-Button + Tabs
 * (Stammdaten/Aufgaben/Berichte/Akte/Projekte). Bearbeiten passiert inline,
 * kein Modal — dadurch bleibt der Kontext erhalten wenn der Nutzer in einen
 * Bericht oder eine Aufgabe abtaucht.
 *
 * Fuer Ansprechpartner (typus=person mit firma_id) fallen die Kunden-Tabs
 * weg, es bleibt nur Stammdaten mit Link zur Firma.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Pencil, Save, X, ListTodo, FileText, FolderTree, LayoutGrid, Building2, User as UserIcon, RefreshCcw, UserPlus, ArrowRightLeft, Eye, EyeOff } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';
import { ConfirmModal } from '@onvice/portal-ui';

import AkteStrukturViewer from '../../components/AkteStrukturViewer';
import { CustomerProjectsTab } from '../../components/CustomerAkteEmbeddedPanel';
import { useFederatedModule } from '../../lib/useFederatedModule';
import type { ComponentType } from 'react';

// stammdaten/bauberichteV2 dynamisch — statische Imports wuerden den
// Federation-Bootstrap-Preload zwingen und einen einzelnen Container-Ausfall
// zum globalen Portal-Boot-Kill machen. Regel:
// [[feedback_federation_no_static_imports]].
type CustomerSummaryData = Record<string, unknown> & { id: string };
type CustomerTaskRow = Record<string, unknown> & { id: string };
type CustomerReportRow = Record<string, unknown> & { id: string };
type StammdatenPublic = {
  CustomerSummary: ComponentType<{ customer: CustomerSummaryData }>;
};
type BauberichteV2Public = {
  CustomerTasksList: ComponentType<{ tasks: CustomerTaskRow[]; returnTo?: string }>;
  CustomerReportsList: ComponentType<{ reports: CustomerReportRow[]; returnTo?: string }>;
};

function ModulOffline({ modul }: { modul: string }) {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
      Modul <b>{modul}</b> ist gerade nicht erreichbar. Sobald es antwortet, erscheint dieser Inhalt automatisch.
    </div>
  );
}

interface ContactRow {
  id: string;
  typus: 'firma' | 'person';
  firma_id: string | null;
  firma_name: string | null;
  kuerzel: string | null;
  ust_id: string | null;
  hr_eintrag: string | null;
  bank_iban: string | null;
  zahlungsziel_tage: number | null;
  anrede: string | null;
  titel: string | null;
  vorname: string | null;
  nachname: string | null;
  position: string | null;
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  ort: string | null;
  land: string | null;
  telefon: string | null;
  mobil: string | null;
  email: string | null;
  notizen: string | null;
  customer_number: string | null;
  kategorien: string[];
}

interface CustomerDetailsRow {
  contact_id: string;
  customer_number: string | null;
  kostenstellen: string[];
  kostenstellen_inaktiv?: string[];
  lat: number | null;
  lng: number | null;
  gps_confirmed_at: string | null;
}

interface OverviewResponse {
  customer: CustomerSummaryData;
  counts: { tasks: number; reports: number; akten_docs: number; projects?: number };
  tasks: CustomerTaskRow[];
  reports: CustomerReportRow[];
}

type Tab = 'stammdaten' | 'aufgaben' | 'berichte' | 'akte' | 'projekte';

interface Form {
  // Firma-Stammdaten
  firma_name: string;
  kuerzel: string;
  ust_id: string;
  hr_eintrag: string;
  bank_iban: string;
  zahlungsziel_tage: string; // im UI als String (leere Eingabe = null)
  // Person-Stammdaten
  anrede: string;
  titel: string;
  vorname: string;
  nachname: string;
  position: string;
  // Adresse
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
  land: string;
  // Kommunikation
  telefon: string;
  mobil: string;
  email: string;
  // Sonstiges
  notizen: string;
  // Kunden-spezifisch (via customer-details Endpoint)
  customer_number: string;
  kostenstellen: string[];
  // Joerg 2026-08-07: Delta-Liste inaktiver KS. Alte KS bleiben in der
  // Voll-Liste sichtbar (Historie), aber werden bei Neuanlage von Aufgaben/
  // Berichten aus dem Picker gefiltert. Ohne UI hier wurde v2.4.1 nie live
  // sichtbar — der KundenStammEditModal war Dead Code.
  kostenstellen_inaktiv: string[];
  lat: string;
  lng: string;
}

const emptyForm: Form = {
  firma_name: '', kuerzel: '', ust_id: '', hr_eintrag: '', bank_iban: '', zahlungsziel_tage: '',
  anrede: '', titel: '', vorname: '', nachname: '', position: '',
  strasse: '', hausnummer: '', plz: '', ort: '', land: '',
  telefon: '', mobil: '', email: '',
  notizen: '',
  customer_number: '',
  kostenstellen: [], kostenstellen_inaktiv: [], lat: '', lng: '',
};

export default function AdressbuchContactAkte() {
  const { id: contactId } = useParams<{ id: string }>();
  const { isAdmin, hasPermission } = useAuth();
  const stammdaten = useFederatedModule<StammdatenPublic>('stammdaten/public');
  const bv2 = useFederatedModule<BauberichteV2Public>('bauberichteV2/public');
  const CustomerSummary = stammdaten?.CustomerSummary ?? null;
  const CustomerTasksList = bv2?.CustomerTasksList ?? null;
  const CustomerReportsList = bv2?.CustomerReportsList ?? null;
  // SD-2026-00021 v3 (Onvice, 2026-08-01): Permission-basiert (adressbuch.write) statt Rollen.
  // customers.create bleibt als Legacy-Fallback fuer alte Rollen-Definitionen.
  const canEdit = isAdmin || hasPermission('adressbuch.write') || hasPermission('customers.create');

  const [contact, setContact] = useState<ContactRow | null>(null);
  const [details, setDetails] = useState<CustomerDetailsRow | null>(null);
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [ksInput, setKsInput] = useState('');
  const [tab, setTab] = useState<Tab>('stammdaten');
  // W5 2026-08-04: Typus-Switch Firma ↔ Einzelperson (mit Bestaetigung).
  const [switchingTypus, setSwitchingTypus] = useState(false);
  const [switchTypusOpen, setSwitchTypusOpen] = useState(false);

  const isKunde = useMemo(() => (contact?.kategorien ?? []).includes('kunde'), [contact]);
  const isAnsprechpartner = contact?.typus === 'person' && !!contact.firma_id;

  const load = useCallback(async () => {
    if (!contactId) return;
    setLoading(true); setError(null);
    try {
      const [c, d, o] = await Promise.all([
        api.get<{ success: boolean; data: ContactRow }>(`/adressbuch/contacts/${contactId}`),
        api.get<{ success: boolean; data: CustomerDetailsRow }>(`/adressbuch/contacts/${contactId}/customer-details`).catch(() => null),
        api.get<{ success: boolean; data: OverviewResponse }>(`/portal/contacts/${contactId}/overview`).catch(() => null),
      ]);
      const cr = c.data;
      setContact(cr);
      setDetails(d?.data ?? null);
      setOverview(o?.data ?? null);
      setForm({
        firma_name: cr?.firma_name ?? '',
        kuerzel: cr?.kuerzel ?? '',
        ust_id: cr?.ust_id ?? '',
        hr_eintrag: cr?.hr_eintrag ?? '',
        bank_iban: cr?.bank_iban ?? '',
        zahlungsziel_tage: cr?.zahlungsziel_tage != null ? String(cr.zahlungsziel_tage) : '',
        anrede: cr?.anrede ?? '',
        titel: cr?.titel ?? '',
        vorname: cr?.vorname ?? '',
        nachname: cr?.nachname ?? '',
        position: cr?.position ?? '',
        strasse: cr?.strasse ?? '',
        hausnummer: cr?.hausnummer ?? '',
        plz: cr?.plz ?? '',
        ort: cr?.ort ?? '',
        land: cr?.land ?? '',
        telefon: cr?.telefon ?? '',
        mobil: cr?.mobil ?? '',
        email: cr?.email ?? '',
        notizen: cr?.notizen ?? '',
        customer_number: d?.data?.customer_number ?? cr?.customer_number ?? '',
        kostenstellen: d?.data?.kostenstellen ?? [],
        kostenstellen_inaktiv: d?.data?.kostenstellen_inaktiv ?? [],
        lat: d?.data?.lat != null ? String(d.data.lat) : '',
        lng: d?.data?.lng != null ? String(d.data.lng) : '',
      });
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { void load(); }, [load]);

  function setField<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function addKs() {
    const v = ksInput.trim();
    if (!v || form.kostenstellen.includes(v)) { setKsInput(''); return; }
    setField('kostenstellen', [...form.kostenstellen, v]);
    setKsInput('');
  }
  function removeKs(v: string) {
    setField('kostenstellen', form.kostenstellen.filter((k) => k !== v));
    // Auch aus der Inaktiv-Delta-Liste raeumen, sonst bleiben Waisen.
    setField('kostenstellen_inaktiv', form.kostenstellen_inaktiv.filter((k) => k !== v));
  }
  function isKsInaktiv(v: string): boolean {
    return form.kostenstellen_inaktiv.includes(v);
  }
  function toggleKsAktiv(v: string) {
    setField(
      'kostenstellen_inaktiv',
      isKsInaktiv(v)
        ? form.kostenstellen_inaktiv.filter((k) => k !== v)
        : [...form.kostenstellen_inaktiv, v],
    );
  }

  async function save() {
    if (!contactId || !contact) return;
    setSaving(true); setSaveError(null);
    try {
      const isFirma = contact.typus === 'firma';
      const isPerson = contact.typus === 'person';
      const zahlungsziel = form.zahlungsziel_tage.trim();
      // Firma-Felder nur bei Firma senden, Person-Felder nur bei Person —
      // das Backend haelt die Felder sonst konsistent leer (analog Neu-Anlegen).
      const payload: Record<string, unknown> = {
        firma_name: isFirma ? (form.firma_name.trim() || null) : null,
        kuerzel: isFirma ? (form.kuerzel.trim() || null) : null,
        ust_id: isFirma ? (form.ust_id.trim() || null) : null,
        hr_eintrag: isFirma ? (form.hr_eintrag.trim() || null) : null,
        bank_iban: isFirma ? (form.bank_iban.trim() || null) : null,
        zahlungsziel_tage: isFirma && zahlungsziel ? Number(zahlungsziel) : null,
        anrede: isPerson ? (form.anrede.trim() || null) : null,
        titel: isPerson ? (form.titel.trim() || null) : null,
        vorname: isPerson ? (form.vorname.trim() || null) : null,
        nachname: isPerson ? (form.nachname.trim() || null) : null,
        position: isPerson ? (form.position.trim() || null) : null,
        strasse: form.strasse.trim() || null,
        hausnummer: form.hausnummer.trim() || null,
        plz: form.plz.trim() || null,
        ort: form.ort.trim() || null,
        land: form.land.trim() || null,
        telefon: form.telefon.trim() || null,
        mobil: form.mobil.trim() || null,
        email: form.email.trim() || null,
        notizen: form.notizen.trim() || null,
      };
      await api.put(`/adressbuch/contacts/${contactId}`, payload);
      if (isKunde) {
        const latNum = form.lat.trim() ? Number(form.lat.trim().replace(',', '.')) : null;
        const lngNum = form.lng.trim() ? Number(form.lng.trim().replace(',', '.')) : null;
        const gpsChanged = latNum !== (details?.lat ?? null) || lngNum !== (details?.lng ?? null);
        await api.put(`/adressbuch/contacts/${contactId}/customer-details`, {
          customer_number: form.customer_number.trim() || null,
          kostenstellen: form.kostenstellen,
          kostenstellen_inaktiv: form.kostenstellen_inaktiv,
          lat: latNum, lng: lngNum,
          gpsConfirmed: !!(gpsChanged && latNum != null && lngNum != null),
        });
      }
      setEditing(false);
      await load();
    } catch (e: unknown) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    setEditing(false); setSaveError(null);
    // Form aus contact/details wieder befuellen
    if (contact) {
      setForm({
        firma_name: contact.firma_name ?? '',
        kuerzel: contact.kuerzel ?? '',
        ust_id: contact.ust_id ?? '',
        hr_eintrag: contact.hr_eintrag ?? '',
        bank_iban: contact.bank_iban ?? '',
        zahlungsziel_tage: contact.zahlungsziel_tage != null ? String(contact.zahlungsziel_tage) : '',
        anrede: contact.anrede ?? '',
        titel: contact.titel ?? '',
        vorname: contact.vorname ?? '',
        nachname: contact.nachname ?? '',
        position: contact.position ?? '',
        strasse: contact.strasse ?? '',
        hausnummer: contact.hausnummer ?? '',
        plz: contact.plz ?? '',
        ort: contact.ort ?? '',
        land: contact.land ?? '',
        telefon: contact.telefon ?? '',
        mobil: contact.mobil ?? '',
        email: contact.email ?? '',
        notizen: contact.notizen ?? '',
        customer_number: details?.customer_number ?? contact.customer_number ?? '',
        kostenstellen: details?.kostenstellen ?? [],
        kostenstellen_inaktiv: details?.kostenstellen_inaktiv ?? [],
        lat: details?.lat != null ? String(details.lat) : '',
        lng: details?.lng != null ? String(details.lng) : '',
      });
    }
  }

  // W5 2026-08-04: Typus zwischen firma/person umschalten. Backend clear't
  // die jeweils inaktiven Felder (Vor-/Nachname vs. firma_name) sobald sie
  // nicht explizit gesetzt sind. Wir muessen aber die neuen Pflichtfelder
  // liefern (Constraint contacts_typus_firma_fields: firma_name NOT NULL;
  // contacts_typus_person_fields: nachname NOT NULL). Fuellen aus dem
  // bestehenden Namensfeld, damit die Zeile nicht leer verpuffet — User
  // kann danach in Ruhe editieren. firma_id wird immer geleert.
  async function switchTypus() {
    if (!contactId || !contact) return;
    setSwitchingTypus(true);
    try {
      const newTypus = contact.typus === 'firma' ? 'person' : 'firma';
      const payload: Record<string, unknown> = { typus: newTypus, firma_id: null };
      if (newTypus === 'firma') {
        const combined = `${contact.vorname ?? ''} ${contact.nachname ?? ''}`.trim();
        payload.firma_name = combined || '(unbenannt)';
      } else {
        payload.nachname = (contact.firma_name ?? '').trim() || '(unbenannt)';
      }
      await api.put(`/adressbuch/contacts/${contactId}`, payload);
      setSwitchTypusOpen(false);
      await load();
    } catch (e: unknown) {
      alert(`Umschalten fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setSwitchingTypus(false);
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-slate-400 gap-2"><Loader2 className="animate-spin" size={16} /> Lädt Kontakt…</div>;
  }
  if (error || !contact) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Link to="/admin/adressbuch?tab=contacts" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-4">
          <ArrowLeft size={14} /> Adressbuch
        </Link>
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-300">{error ?? 'Kontakt nicht gefunden'}</div>
      </div>
    );
  }

  const displayName = contact.typus === 'firma'
    ? contact.firma_name ?? '(unbenannte Firma)'
    : `${contact.anrede ? contact.anrede + ' ' : ''}${contact.titel ? contact.titel + ' ' : ''}${contact.vorname ?? ''} ${contact.nachname ?? ''}`.trim();

  const tabs: Tab[] = ['stammdaten'];
  if (isKunde) tabs.push('aufgaben', 'berichte', 'akte', 'projekte');

  const returnTo = `/admin/adressbuch/contacts/${contactId}`;

  return (
    <div className="min-h-screen">
      {/* Sticky Header */}
      <div className="sticky top-14 z-20 bg-card/95 backdrop-blur border-b border-default">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/admin/adressbuch?tab=contacts" className="text-slate-400 hover:text-slate-200 p-1">
            <ArrowLeft size={18} />
          </Link>
          <div className="text-2xl flex-shrink-0">{contact.typus === 'firma' ? '🏢' : '👤'}</div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-foreground truncate">{displayName}</h1>
            <div className="text-xs text-muted-foreground flex gap-3 flex-wrap">
              {isKunde && form.customer_number && <span className="font-mono">{form.customer_number}</span>}
              {contact.kategorien.map((k) => <span key={k} className="text-brand-400">· {k}</span>)}
            </div>
          </div>
          {canEdit && !editing && contact.typus === 'firma' && (
            <Link
              to={`/admin/adressbuch/contacts/new?typus=person&kategorie=ansprechpartner&firma_id=${contact.id}`}
              className="inline-flex items-center gap-1 px-3 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm"
              title="Ansprechpartner fuer diese Firma anlegen"
            >
              <UserPlus size={14} /> Ansprechpartner
            </Link>
          )}
          {/* 2026-08-31 (Joerg): auch Ansprechpartner duerfen umgeschaltet werden —
              das Modal warnt, dass die Firma-Zuordnung dabei geloest wird
              (Backend leert firma_id beim Typus-Wechsel ohnehin). */}
          {canEdit && !editing && (
            <button
              onClick={() => setSwitchTypusOpen(true)}
              disabled={switchingTypus}
              title={contact.typus === 'firma' ? 'Zu Einzelperson umschalten' : 'Zu Firma umschalten'}
              className="inline-flex items-center gap-1 px-3 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm disabled:opacity-50"
            >
              <ArrowRightLeft size={14} />
              {contact.typus === 'firma' ? 'Zu Person' : 'Zu Firma'}
            </button>
          )}
          {canEdit && !editing && (
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 px-3 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm">
              <Pencil size={14} /> Bearbeiten
            </button>
          )}
          {editing && (
            <div className="flex gap-2">
              <button onClick={cancelEdit} disabled={saving} className="inline-flex items-center gap-1 px-3 py-2 rounded border border-slate-700 text-slate-300 text-sm hover:bg-slate-800">
                <X size={14} /> Abbrechen
              </button>
              <button onClick={() => void save()} disabled={saving} className="inline-flex items-center gap-1 px-3 py-2 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
              </button>
            </div>
          )}
        </div>

        {/* Tab-Bar */}
        {tabs.length > 1 && (
          <nav className="max-w-6xl mx-auto px-4 border-t border-slate-800/60">
            <ul className="flex flex-wrap gap-1 -mb-px">
              {tabs.map((t) => {
                const label = t === 'stammdaten' ? 'Stammdaten'
                  : t === 'aufgaben' ? `Aufgaben${overview ? ' (' + overview.counts.tasks + ')' : ''}`
                  : t === 'berichte' ? `Berichte${overview ? ' (' + overview.counts.reports + ')' : ''}`
                  : t === 'akte' ? `Akte${overview ? ' (' + overview.counts.akten_docs + ')' : ''}`
                  : `Projekte${overview?.counts.projects != null ? ' (' + overview.counts.projects + ')' : ''}`;
                const active = tab === t;
                return (
                  <li key={t}>
                    <button
                      type="button"
                      onClick={() => setTab(t)}
                      className={`px-3 py-2 text-sm border-b-2 transition-colors ${
                        active ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {saveError && <div className="mb-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">{saveError}</div>}

        {tab === 'stammdaten' && (
          <div className="space-y-6">
            {/* Basis-Felder */}
            <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-4">
              <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider flex items-center gap-2">
                {contact.typus === 'firma' ? <Building2 size={14} /> : <UserIcon size={14} />}
                {contact.typus === 'firma' ? 'Firma' : 'Person'}
              </h2>
              {contact.typus === 'firma' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FieldOrText label="Firmenname" value={form.firma_name} editing={editing} onChange={(v) => setField('firma_name', v)} className="sm:col-span-2" />
                  {isKunde && <FieldOrText label="Kundennummer" value={form.customer_number} editing={editing} onChange={(v) => setField('customer_number', v)} monospace placeholder="K-00001 (auto)" />}
                  <FieldOrText label="Kuerzel" value={form.kuerzel} editing={editing} onChange={(v) => setField('kuerzel', v)} monospace placeholder="z.B. MEIER" />
                  <FieldOrText label="USt-IdNr." value={form.ust_id} editing={editing} onChange={(v) => setField('ust_id', v)} monospace placeholder="DE123456789" />
                  <FieldOrText label="HR-Eintrag" value={form.hr_eintrag} editing={editing} onChange={(v) => setField('hr_eintrag', v)} placeholder="HRB 12345 Berlin" />
                  <FieldOrText label="Zahlungsziel (Tage)" value={form.zahlungsziel_tage} editing={editing} onChange={(v) => setField('zahlungsziel_tage', v.replace(/[^0-9]/g, ''))} placeholder="14" />
                  <FieldOrText label="IBAN" value={form.bank_iban} editing={editing} onChange={(v) => setField('bank_iban', v)} monospace placeholder="DE89 3704 …" className="sm:col-span-3" />
                </div>
              )}
              {contact.typus === 'person' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FieldOrText label="Anrede" value={form.anrede} editing={editing} onChange={(v) => setField('anrede', v)} placeholder="Herr / Frau" />
                  <FieldOrText label="Titel" value={form.titel} editing={editing} onChange={(v) => setField('titel', v)} placeholder="Dr., Prof." />
                  <div />
                  <FieldOrText label="Vorname" value={form.vorname} editing={editing} onChange={(v) => setField('vorname', v)} />
                  <FieldOrText label="Nachname" value={form.nachname} editing={editing} onChange={(v) => setField('nachname', v)} />
                  <FieldOrText label="Position" value={form.position} editing={editing} onChange={(v) => setField('position', v)} placeholder="z.B. Einkauf" />
                  {contact.firma_id && (
                    <div className="sm:col-span-3">
                      <div className="text-xs text-slate-500 mb-1">Firma</div>
                      <Link to={`/admin/adressbuch/contacts/${contact.firma_id}`} className="text-brand-300 hover:text-brand-200 underline">
                        🏢 zur Firma
                      </Link>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="sm:col-span-3">
                  <FieldOrText label="Strasse" value={form.strasse} editing={editing} onChange={(v) => setField('strasse', v)} />
                </div>
                <FieldOrText label="Hausnummer" value={form.hausnummer} editing={editing} onChange={(v) => setField('hausnummer', v)} />
                <FieldOrText label="PLZ" value={form.plz} editing={editing} onChange={(v) => setField('plz', v)} />
                <div className="sm:col-span-2">
                  <FieldOrText label="Ort" value={form.ort} editing={editing} onChange={(v) => setField('ort', v)} />
                </div>
                <FieldOrText label="Land" value={form.land} editing={editing} onChange={(v) => setField('land', v)} placeholder="Deutschland" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FieldOrText label="Telefon" value={form.telefon} editing={editing} onChange={(v) => setField('telefon', v)} type="tel" />
                <FieldOrText label="Mobil" value={form.mobil} editing={editing} onChange={(v) => setField('mobil', v)} type="tel" />
                <FieldOrText label="E-Mail" value={form.email} editing={editing} onChange={(v) => setField('email', v)} type="email" />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Notizen</div>
                {editing ? (
                  <textarea
                    value={form.notizen}
                    onChange={(e) => setField('notizen', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm"
                    placeholder="Interne Notizen zum Kontakt…"
                  />
                ) : (
                  <div className="text-sm text-slate-200 whitespace-pre-wrap">{form.notizen || <span className="italic text-slate-500">—</span>}</div>
                )}
              </div>
            </section>

            {isKunde && (
              <>
                {/* GPS + Kostenstellen — nur bei Kunde */}
                <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-3">
                  <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">GPS + Kostenstellen</h2>
                  {editing ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FieldOrText label="Breite (lat)" value={form.lat} editing onChange={(v) => setField('lat', v)} placeholder="52.520008" />
                        <FieldOrText label="Länge (lng)" value={form.lng} editing onChange={(v) => setField('lng', v)} placeholder="13.404954" />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Kostenstellen</div>
                        {/* Joerg 2026-08-07: Auge-Toggle pro KS. Aktiv (Auge auf) = im Picker
                             bei Neuanlage waehlbar. Inaktiv (Auge zu) = ausgeblendet + durchgestrichen,
                             bleibt aber am Kunden fuer Historie. X entfernt aus beiden Listen. */}
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {form.kostenstellen.length === 0 && <span className="text-xs text-slate-500 italic">keine</span>}
                          {form.kostenstellen.map((ks) => {
                            const inaktiv = isKsInaktiv(ks);
                            return (
                              <span
                                key={ks}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs ${
                                  inaktiv
                                    ? 'bg-slate-900/60 border-slate-700 text-slate-500 line-through'
                                    : 'bg-slate-800 border-slate-700 text-slate-200'
                                }`}
                              >
                                {ks}
                                {inaktiv && <span className="not-italic no-underline text-slate-500">(inaktiv)</span>}
                                <button
                                  type="button"
                                  onClick={() => toggleKsAktiv(ks)}
                                  title={inaktiv ? 'Wieder aktiv setzen' : 'Als inaktiv markieren (aus Picker ausblenden)'}
                                  className={`no-underline ${inaktiv ? 'text-slate-500 hover:text-emerald-400' : 'text-slate-400 hover:text-amber-400'}`}
                                >
                                  {inaktiv ? <EyeOff size={12} /> : <Eye size={12} />}
                                </button>
                                <button type="button" onClick={() => removeKs(ks)} title="KS ganz vom Kunden entfernen" className="no-underline text-slate-500 hover:text-rose-400"><X size={12} /></button>
                              </span>
                            );
                          })}
                        </div>
                        <div className="flex gap-2">
                          <input value={ksInput} onChange={(e) => setKsInput(e.target.value)}
                                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKs(); } }}
                                 placeholder="z.B. KS-100"
                                 className="flex-1 px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
                          <button type="button" onClick={addKs} disabled={!ksInput.trim()} className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-40 text-sm">+ Hinzufügen</button>
                        </div>
                        <div className="text-xs text-slate-500 mt-1.5">
                          Klick auf das Auge macht eine KS inaktiv — sie bleibt am Kunden sichtbar,
                          wird aber bei neuen Aufgaben/Berichten nicht mehr zur Auswahl angeboten.
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-slate-300 space-y-2">
                      <div><span className="text-slate-500">GPS:</span> {form.lat && form.lng ? <>{form.lat}, {form.lng}</> : <span className="italic text-slate-500">nicht gesetzt</span>}</div>
                      <div>
                        <span className="text-slate-500">Kostenstellen:</span>{' '}
                        {form.kostenstellen.length === 0
                          ? <span className="italic text-slate-500">keine</span>
                          : form.kostenstellen.map((ks) => {
                              const inaktiv = isKsInaktiv(ks);
                              return (
                                <span
                                  key={ks}
                                  className={`inline-block ml-1 px-2 py-0.5 rounded-full border text-xs ${
                                    inaktiv
                                      ? 'bg-slate-900/60 border-slate-700 text-slate-500 line-through'
                                      : 'bg-slate-800 border-slate-700'
                                  }`}
                                >
                                  {ks}
                                  {inaktiv && <span className="ml-1 not-italic no-underline text-slate-500">(inaktiv)</span>}
                                </span>
                              );
                            })}
                      </div>
                    </div>
                  )}
                </section>
              </>
            )}

            {!isKunde && isAnsprechpartner && (
              <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5">
                <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider mb-2">Letzte Kontakte</h2>
                <p className="text-xs text-slate-500 italic">
                  Zeigt künftig die letzten Aufgaben, Berichte und Termine dieses Ansprechpartners.
                  Wird geliefert, sobald diese direkt mit Kontakten verknüpft werden.
                </p>
              </section>
            )}
          </div>
        )}

        {tab === 'aufgaben' && overview && (
          CustomerTasksList
            ? <CustomerTasksList tasks={overview.tasks} returnTo={returnTo} />
            : <ModulOffline modul="Bauberichte" />
        )}

        {tab === 'berichte' && overview && (
          CustomerReportsList
            ? <CustomerReportsList reports={overview.reports} returnTo={returnTo} />
            : <ModulOffline modul="Bauberichte" />
        )}

        {tab === 'akte' && overview && (
          <AkteStrukturViewer source={{ kind: 'customer', customerId: overview.customer.id }} configTyp="kunde" />
        )}

        {/* SD-2026-00022 (Onvice, 2026-07-31): Projekte-Panel eingebettet.
            Vorher stand hier "Projekte-Ansicht folgt" + Link auf Legacy-/kunden/:id,
            das inzwischen ein Redirect zurueck auf /admin/adressbuch ist. */}
        {tab === 'projekte' && overview && (
          <CustomerProjectsTab customerId={overview.customer.id} />
        )}

        {tab === 'stammdaten' && !editing && isKunde && overview && (
          <section className="mt-6 rounded-lg border border-slate-800 bg-slate-900/40 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">Uebersicht</h2>
              <button onClick={() => void load()} className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
                <RefreshCcw size={12} /> Neu laden
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Kpi icon={ListTodo}   label="Aufgaben"  value={overview.counts.tasks} />
              <Kpi icon={FileText}   label="Berichte"  value={overview.counts.reports} />
              <Kpi icon={FolderTree} label="Dokumente" value={overview.counts.akten_docs} />
              <Kpi icon={LayoutGrid} label="Projekte"  value={overview.counts.projects ?? 0} />
            </div>
            {/* Kurz-Stammdaten aus CustomerSummary (Legacy, read-only) */}
            <div className="mt-4">
              {CustomerSummary
                ? <CustomerSummary customer={overview.customer} />
                : <ModulOffline modul="Stammdaten" />}
            </div>
          </section>
        )}
      </div>

      <ConfirmModal
        open={switchTypusOpen}
        title={contact.typus === 'firma' ? 'Zu Einzelperson umschalten?' : 'Zu Firma umschalten?'}
        message={
          contact.typus === 'firma'
            ? `Dieser Kontakt wird zu einer Einzelperson. Der Firmenname wird geleert; Vor-/Nachname werden nach dem Umschalten pflegbar. Der Kunde bleibt bestehen und behaelt Aufgaben, Berichte und Akten.`
            : `Dieser Kontakt wird zu einer Firma. Vor-/Nachname werden geleert; der Firmenname wird nach dem Umschalten pflegbar. Der Kunde bleibt bestehen und behaelt Aufgaben, Berichte und Akten.${isAnsprechpartner ? ' Die Zuordnung zur bisherigen Firma wird dabei gelöst — der Kontakt wird eine eigenständige Firma.' : ''}`
        }
        confirmLabel="Umschalten"
        busy={switchingTypus}
        onClose={() => setSwitchTypusOpen(false)}
        onConfirm={() => void switchTypus()}
      />
    </div>
  );
}

function FieldOrText({ label, value, editing, onChange, type = 'text', placeholder, className, monospace }: {
  label: string; value: string; editing: boolean;
  onChange: (v: string) => void;
  type?: string; placeholder?: string; className?: string; monospace?: boolean;
}) {
  return (
    <div className={className}>
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      {editing ? (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
               className={`w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm ${monospace ? 'font-mono' : ''}`} />
      ) : (
        <div className={`text-sm text-slate-100 ${monospace ? 'font-mono' : ''}`}>{value || <span className="text-slate-500 italic">—</span>}</div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value }: {
  icon: React.ComponentType<{ size?: number; className?: string }>; label: string; value: number;
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 flex items-center gap-3">
      <Icon size={18} className="text-brand-400 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-lg font-semibold text-foreground leading-none">{value}</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{label}</div>
      </div>
    </div>
  );
}
